import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Layout,
  Row,
  Col,
  Form,
  Select,
  Button,
  DatePicker,
  Spin,
  Radio,
  Modal,
  Segmented,
} from "antd";
import dayjs from "dayjs";
import { CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  GetCustomerList,
  GetCustomerUploadMasterList,
  GetProductStatusCount,
} from "../../../utils/api.constant";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import StackedBarChart from "./chart";
import styles from "./dashboard.module.css";
import { TContext, TDropdownOptions } from "../../../types/config";
import { UserContext } from "../../../context";
import { TFileManagerMaster } from "../../../types";
import { RecentUploadsList } from "./file-management-list";

const { Title } = Typography;

function DashboardView() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const context: TContext = useContext(UserContext);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [fileOptions, setFileOptions] = useState<TDropdownOptions[]>([]);
  const [data, setData] = useState<TFileManagerMaster[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(0);
  const [chartData, setChartData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fileLoader, setFileLoader] = useState(false);
  const [fromDate, setFromDate] = useState(dayjs().subtract(7, "day"));
  const [toDate, setToDate] = useState(dayjs());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filterMode, setFilterMode] = useState<"time" | "file">("time");
  const [selectedDateType, setSelectedDateType] = useState<string>("last7days");

  const colorPalette = [
    "#d8811dff",
    "#9d7695ff",
    "#447594ff",
    "#CCCCCC",
    "#6c7869",
    "#dfc6af",
    "#982424ff",
    "#37447cff",
    "#e39e83",
    "#3d7475",
    "#8FBF59",
  ];

  const generateRandomMatteColor = () => {
    const hue = Math.floor(Math.random() * 360);
    const saturation = Math.floor(Math.random() * 25) + 35;
    const lightness = Math.floor(Math.random() * 30) + 40;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const fetchCustomerOptions = async () => {
    try {
      setIsLoading(true);
      const response: any = await post(GetCustomerList, {
        data: { currentPage: 1, pageSize: -1 },
      });

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        const mapped = Utils.mapDropdownOptions(response.data);
        setCustomerOptions(mapped);

        if (mapped.length > 0) {
          const firstId = mapped[0].value;
          setSelectedId(firstId);
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response?.dataResponse?.description || "Failed to load list",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomerUploadMaster = async () => {
    try {
      setFileLoader(true);
      const response: any = await post(GetCustomerUploadMasterList, {
        data: { currentPage: 1, pageSize: -1 },
      });

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        const mapped = Utils.mapDropdownOptions(
          response.data,
          "id",
          "uploadFileName"
        );
        mapped.push({ label: "All", value: 0 });
        setData(response?.data);
        setFileOptions(mapped);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response?.dataResponse?.description || "Failed to load list",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFileLoader(false);
    }
  };

  const fetchProductStatus = async (customerId: number) => {
    try {
      setIsLoading(true);
      const payload = {
        Data: {
          customerId,
          fromDate:
            filterMode === "time" ? fromDate.format("DD-MMM-YYYY") : null,
          toDate: filterMode === "time" ? toDate.format("DD-MMM-YYYY") : null,
          customerUploadMasterId: filterMode === "file" ? selectedFileId : null,
        },
      };
      const response: any = await post(GetProductStatusCount, payload);

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        const stages = response.data.stage || [];

        const allProducts = new Set<string>();
        stages.forEach((s: any) =>
          s.product.forEach((p: any) => allProducts.add(p.airtechModel))
        );
        const labels = stages.map((s: any) => s.stageName);
        const productNames = Array.from(allProducts);

        const datasets = productNames.map((airtechModel, idx) => ({
          label: airtechModel,
          data: stages.map((stage: any) => {
            const matchingProducts = stage.product.filter(
              (p: any) => p.airtechModel === airtechModel
            );
            const totalCount = matchingProducts.reduce(
              (sum: number, p: any) => sum + (p.count || 0),
              0
            );
            return totalCount;
          }),
          backgroundColor: colorPalette[idx]
            ? colorPalette[idx]
            : generateRandomMatteColor(),

          borderWidth: 1,
          barThickness: 40,
        }));

        setChartData({ labels, datasets });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response?.dataResponse?.description || "Failed to load data",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerOptions();
    fetchCustomerUploadMaster();
  }, []);

  useEffect(() => {
    if (selectedId) {
      fetchProductStatus(selectedId);
    }
  }, [fromDate, toDate, selectedId, selectedFileId, filterMode]);

  const handleDateRangeChange = (e: any) => {
    const { value } = e.target;
    setSelectedDateType(value);
    switch (value) {
      case "today":
        setFromDate(dayjs());
        setToDate(dayjs());
        break;
      case "last7days":
        setFromDate(dayjs().subtract(7, "day"));
        setToDate(dayjs());
        break;
      case "currentMonth":
        setFromDate(dayjs().startOf("month"));
        setToDate(dayjs());
        break;
      case "custom":
        break;
      default:
        break;
    }
  };

  const getDurationString = () => {
    if (filterMode === "file") {
      const file = fileOptions.find((f) => f.value === selectedFileId);
      return file ? `File: ${file.label}` : "";
    } else {
      const fromStr = fromDate.format("DD-MMM-YYYY");
      const toStr = toDate.format("DD-MMM-YYYY");
      switch (selectedDateType) {
        case "today":
          return "Today";
        case "last7days":
          return `In Last 7 Days (${fromStr} to ${toStr})`;
        case "currentMonth":
          return `Current Month (${fromDate.format("MMMM")})`;
        case "custom":
          return `Custom Date Range (${fromStr} to ${toStr})`;
        default:
          return "";
      }
    }
  };

  return (
    <Layout className={styles.dashboardLayout}>
      <Form layout="vertical">
        <Row gutter={16} className={styles.filterRow}>
          <Col xs={24} sm={12} md={4} lg={4}>
            <Form.Item label="Customer">
              <Select
                disabled={isLoading}
                placeholder="Select Customer"
                options={customerOptions}
                value={selectedId ?? undefined}
                onChange={(value: number) => {
                  setSelectedId(value);
                }}
                showSearch
                filterOption={(input, option: any) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: "100%" }}
                size="middle"
              />
            </Form.Item>
          </Col>

          <Col
            xs={24}
            sm={12}
            md={6}
            lg={6}
            style={{ display: "flex", alignItems: "center" }}
          >
            <div>
              <Segmented
                options={[
                  { label: "Duration", value: "time" },
                  { label: "File", value: "file" },
                ]}
                value={filterMode}
                onChange={(value) => {
                  setFilterMode(value as "time" | "file");
                  if (value === "time") {
                    setSelectedFileId(0);
                  }
                }}
              />
            </div>
          </Col>

          {/* time wise filter */}
          {filterMode === "time" && (
            <Col
              xs={24}
              sm={24}
              md={10}
              lg={10}
              className={styles.dateRangeCol}
            >
              <Radio.Group
                onChange={handleDateRangeChange}
                value={selectedDateType}
                optionType="button"
              >
                <Radio.Button value="today">Today</Radio.Button>
                <Radio.Button value="last7days">Last 7 Days</Radio.Button>
                <Radio.Button value="currentMonth">Current Month</Radio.Button>
                <Radio.Button
                  value="custom"
                  onClick={() => setIsModalVisible(true)}
                >
                  Custom
                </Radio.Button>
              </Radio.Group>
            </Col>
          )}

          {/* file wise filter */}
          {filterMode === "file" && (
            <Col xs={24} sm={24} md={10} lg={10}>
              <Form.Item label="File">
                <Select
                  disabled={isLoading}
                  placeholder="Select File"
                  options={fileOptions}
                  value={selectedFileId ?? undefined}
                  onChange={(value: number) => {
                    setSelectedFileId(value);
                  }}
                  showSearch
                  filterOption={(input, option: any) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: "100%" }}
                  size="middle"
                />
              </Form.Item>
            </Col>
          )}
          <Col
            xs={24}
            sm={12}
            md={4}
            lg={4}
            style={{
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Button
              disabled
              type="primary"
              size="small"
              block
              icon={<DownloadOutlined></DownloadOutlined>}
            >
              Download
            </Button>
          </Col>
        </Row>
      </Form>
      <div className={styles.chartListWrapper}>
        <div className={styles.chartWrapper}>
          <StackedBarChart
            chartData={chartData}
            selectedId={selectedId}
            fetchProductStatus={fetchProductStatus}
            isLoading={isLoading}
            duration={getDurationString()}
          />
        </div>

        <div className={styles.listWrapper}>
          <RecentUploadsList
            data={data}
            context={context}
            loading={fileLoader}
          />
        </div>
      </div>

      <Modal
        title="Select Custom Date Range"
        visible={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        style={{ padding: "20px" }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="From Date">
              <DatePicker
                format="DD-MMM-YYYY"
                value={fromDate}
                onChange={(date) => setFromDate(date)}
                placeholder="Select Date"
                style={{ width: "100%" }}
                size="small"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="To Date">
              <DatePicker
                maxDate={dayjs()}
                format="DD-MMM-YYYY"
                value={toDate}
                onChange={(date) => setToDate(date)}
                placeholder="Select Date"
                style={{ width: "100%" }}
                size="small"
              />
            </Form.Item>
          </Col>
        </Row>
      </Modal>
    </Layout>
  );
}

export default DashboardView;
